import { ReactNode } from 'react';
import { Table } from '@tanstack/react-table';
import { Check, X } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';

type BulkSelectionBarProps<TData> = {
  table: Table<TData>;
  /** How many rows are selected (the reviewable ones). */
  count: number;
  onApprove: () => void;
  onReject: () => void;
  /** Which of the two is running (its button spins, both are disabled). */
  running?: 'approve' | 'reject';
  /** Anything else before "Clear selection". */
  children?: ReactNode;
};

/**
 * The contextual toolbar of a review list (HR Module Prototype, Component Map › Bulk actions, Review
 * changes round 2): while rows are selected the table toolbar becomes "N requests selected · Approve ·
 * Reject · Clear selection", at the same height, so the table doesn't move.
 */
export const BulkSelectionBar = <TData,>({
  table,
  count,
  onApprove,
  onReject,
  running,
  children,
}: BulkSelectionBarProps<TData>) => {
  const { t } = useTranslation();
  const isRunning = Boolean(running);
  const allSelected = table.getIsAllPageRowsSelected();

  return (
    <div
      role="toolbar"
      aria-label={t('BULK_ACTIONS')}
      className="flex min-h-8 flex-wrap items-center gap-2 rounded-md bg-accent py-0.5 pl-3 pr-1 text-sm text-accent-foreground"
    >
      <Checkbox
        checked={allSelected ? true : 'indeterminate'}
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(Boolean(value))}
        aria-label={t('SELECT_ALL')}
        className="mr-1"
      />
      <b className="font-semibold">{t('REQUESTS_SELECTED_COUNT', { count })}</b>
      <Button
        type="button"
        size="sm"
        className="h-7"
        loading={running === 'approve'}
        disabled={isRunning}
        onClick={onApprove}
      >
        <Check className="size-4" />
        {t('APPROVE')}
      </Button>
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-7"
        loading={running === 'reject'}
        disabled={isRunning}
        onClick={onReject}
      >
        <X className="size-4" />
        {t('REJECT')}
      </Button>
      {children}
      <span className="flex-1" />
      <Button
        type="button"
        size="sm"
        variant="ghost"
        className="h-7"
        disabled={isRunning}
        onClick={() => table.resetRowSelection()}
      >
        {t('CLEAR_SELECTION')}
      </Button>
    </div>
  );
};
